import { useEffect, useMemo, useState, type ChangeEvent } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Chip,
  useTheme,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import { PieChart } from "@mui/x-charts/PieChart";
import dayjs from "dayjs";

import type { JobAnalyticsEventName, JobAnalyticsRow } from "./tmHubReport.types";
import { EVENT_NAME_LABEL, INTEREST_WEIGHT } from "./tmHubReport.types";
import {
  JobAnalyticsFetchError,
  fetchJobAnalyticsEvents,
} from "./TMHubReport.service";

/** Roles the user expressed interest in (compare credits source + target). */
function interestTargets(ev: JobAnalyticsRow): { title: string; id: string }[] {
  if (ev.eventName === "COMPARE_POSITION") {
    const out: { title: string; id: string }[] = [];
    if (ev.sourcePos && ev.sourcePosId)
      out.push({ title: ev.sourcePos, id: ev.sourcePosId });
    if (ev.targetPos && ev.targetPosId)
      out.push({ title: ev.targetPos, id: ev.targetPosId });
    return out;
  }
  if (ev.targetPos && ev.targetPosId)
    return [{ title: ev.targetPos, id: ev.targetPosId }];
  return [];
}

function interestIncludesTarget(ev: JobAnalyticsRow, roleTitle: string): boolean {
  const q = roleTitle.trim().toLowerCase();
  if (!q) return false;
  return interestTargets(ev).some((t) => t.title.toLowerCase() === q);
}

const filterPositionOptions = createFilterOptions<string>({
  matchFrom: "any",
  stringify: (o) => o,
});

const StatCard = ({
  title,
  value,
  hint,
}: {
  title: string;
  value: string | number;
  hint?: string;
}) => (
  <Card
    variant="outlined"
    className="min-w-[140px] flex-1"
    sx={{ bgcolor: "background.paper", borderColor: "divider" }}
  >
    <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {title}
      </Typography>
      <Typography variant="h5" component="p" fontWeight={600} sx={{ mt: 0.5 }}>
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }} display="block">
          {hint}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const TMHubReport = () => {
  const theme = useTheme();
  const [eventName, setEventName] = useState<JobAnalyticsEventName | "">("");
  const [dateFrom, setDateFrom] = useState(() =>
    dayjs().subtract(1, "month").format("YYYY-MM-DD")
  );
  const [dateTo, setDateTo] = useState(() => dayjs().format("YYYY-MM-DD"));
  const [focusTargetRole, setFocusTargetRole] = useState<string | null>(null);
  const [events, setEvents] = useState<JobAnalyticsRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  const dateInputBounds = useMemo(() => {
    const from = dayjs(dateFrom);
    const to = dayjs(dateTo);
    return {
      fromMin: to.subtract(1, "month").format("YYYY-MM-DD"),
      fromMax: to.format("YYYY-MM-DD"),
      toMin: from.format("YYYY-MM-DD"),
      toMax: from.add(1, "month").format("YYYY-MM-DD"),
    };
  }, [dateFrom, dateTo]);

  const handleDateFromChange = (e: ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (!v) return;
    let from = dayjs(v);
    let to = dayjs(dateTo);
    if (from.isAfter(to, "day")) {
      to = from;
    }
    const maxEnd = from.add(1, "month");
    if (to.isAfter(maxEnd, "day")) {
      to = maxEnd;
    }
    setDateFrom(from.format("YYYY-MM-DD"));
    setDateTo(to.format("YYYY-MM-DD"));
  };

  const handleDateToChange = (e: ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (!v) return;
    let to = dayjs(v);
    let from = dayjs(dateFrom);
    if (to.isBefore(from, "day")) {
      from = to;
    }
    const maxEnd = from.add(1, "month");
    if (to.isAfter(maxEnd, "day")) {
      to = maxEnd;
    }
    setDateFrom(from.format("YYYY-MM-DD"));
    setDateTo(to.format("YYYY-MM-DD"));
  };

  useEffect(() => {
    const start = dayjs(dateFrom);
    const end = dayjs(dateTo);
    if (start.isAfter(end, "day")) {
      setError("The start date must be on or before the end date.");
      setEvents([]);
      setLoading(false);
      return;
    }
    if (end.isAfter(start.add(1, "month"), "day")) {
      setError("Date range must not exceed 1 month.");
      setEvents([]);
      setLoading(false);
      return;
    }

    const ac = new AbortController();
    setLoading(true);
    setError(null);

    fetchJobAnalyticsEvents(dateFrom, dateTo, ac.signal)
      .then((data) => {
        if (ac.signal.aborted) return;
        setEvents(data);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return;
        setEvents([]);
        setLoading(false);
        if (e instanceof JobAnalyticsFetchError) {
          setError(e.message);
        } else if (e instanceof Error) {
          setError(e.message);
        } else {
          setError("Failed to load job analytics.");
        }
      });

    return () => ac.abort();
  }, [dateFrom, dateTo, retryKey]);

  const positionOptions = useMemo(() => {
    const s = new Set<string>();
    for (const ev of events) {
      if (ev.currentPos) s.add(ev.currentPos);
      if (ev.sourcePos) s.add(ev.sourcePos);
      if (ev.targetPos) s.add(ev.targetPos);
    }
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [events]);

  const baseFiltered = useMemo(() => {
    return events.filter((ev) => {
      if (eventName && ev.eventName !== eventName) return false;
      return true;
    });
  }, [events, eventName]);

  const hiringEvents = useMemo(() => {
    if (!focusTargetRole) return [];
    return baseFiltered.filter((ev) =>
      interestIncludesTarget(ev, focusTargetRole)
    );
  }, [baseFiltered, focusTargetRole]);

  const kpis = useMemo(() => {
    const userSet = new Set(baseFiltered.map((e) => e.userId));
    const targetRoles = new Set<string>();
    for (const ev of baseFiltered) {
      for (const p of interestTargets(ev)) targetRoles.add(p.title);
    }
    const avgPerUser =
      userSet.size > 0 ? (baseFiltered.length / userSet.size).toFixed(1) : "0";

    return {
      totalEvents: baseFiltered.length,
      uniqueUsers: userSet.size,
      distinctTargetRoles: targetRoles.size,
      avgPerUser,
    };
  }, [baseFiltered]);

  const eventMix = useMemo(() => {
    const counts: Record<JobAnalyticsEventName, number> = {
      VIEW_POSITION: 0,
      FAVORITE: 0,
      COMPARE_POSITION: 0,
    };
    for (const ev of baseFiltered) counts[ev.eventName] += 1;
    return counts;
  }, [baseFiltered]);

  const topTargetJobs = useMemo(() => {
    type Agg = {
      id: string;
      positionTitle: string;
      views: number;
      favorites: number;
      compares: number;
      score: number;
    };
    const map = new Map<string, Agg>();

    for (const ev of baseFiltered) {
      const touches = interestTargets(ev);
      const w = INTEREST_WEIGHT[ev.eventName];
      for (const pos of touches) {
        const key = pos.title;
        if (!map.has(key)) {
          map.set(key, {
            id: pos.id,
            positionTitle: key,
            views: 0,
            favorites: 0,
            compares: 0,
            score: 0,
          });
        }
        const row = map.get(key)!;
        if (ev.eventName === "VIEW_POSITION") row.views += 1;
        else if (ev.eventName === "FAVORITE") row.favorites += 1;
        else row.compares += 1;
        row.score += w;
      }
    }

    const list = Array.from(map.values()).sort((a, b) => b.score - a.score);
    const top = list.slice(0, 12);

    const short = (s: string, n = 22) =>
      s.length <= n ? s : `${s.slice(0, n)}…`;

    return {
      labels: top.map((r) => short(r.positionTitle)),
      views: top.map((r) => r.views),
      favorites: top.map((r) => r.favorites),
      compares: top.map((r) => r.compares),
    };
  }, [baseFiltered]);

  const timeline = useMemo(() => {
    const byDay = new Map<string, number>();
    for (const ev of baseFiltered) {
      const k = dayjs(ev.createdAt).format("YYYY-MM-DD");
      byDay.set(k, (byDay.get(k) || 0) + 1);
    }
    const keys = Array.from(byDay.keys()).sort();
    return {
      labels: keys.map((k) => dayjs(k).format("MMM D")),
      values: keys.map((k) => byDay.get(k) || 0),
    };
  }, [baseFiltered]);

  const hiringRanked = useMemo(() => {
    type Acc = {
      id: string;
      events: number;
      views: number;
      favorites: number;
      compares: number;
      interestScore: number;
      currentPosSet: Set<string>;
      otherInterestSet: Set<string>;
    };
    const map = new Map<string, Acc>();
    const roleLower = (focusTargetRole || "").trim().toLowerCase();
    for (const ev of hiringEvents) {
      if (!map.has(ev.userId)) {
        map.set(ev.userId, {
          id: ev.userId,
          events: 0,
          views: 0,
          favorites: 0,
          compares: 0,
          interestScore: 0,
          currentPosSet: new Set(),
          otherInterestSet: new Set(),
        });
      }
      const u = map.get(ev.userId)!;
      u.events += 1;
      if (ev.eventName === "VIEW_POSITION") u.views += 1;
      else if (ev.eventName === "FAVORITE") u.favorites += 1;
      else u.compares += 1;
      const w = INTEREST_WEIGHT[ev.eventName];
      const hits = interestTargets(ev).filter(
        (t) => t.title.toLowerCase() === roleLower
      ).length;
      u.interestScore += w * hits;
      if (ev.currentPos) u.currentPosSet.add(ev.currentPos.trim());
      for (const t of interestTargets(ev)) {
        if (t.title.toLowerCase() !== roleLower) u.otherInterestSet.add(t.title);
      }
    }
    const sorted = Array.from(map.values())
      .sort((a, b) => b.interestScore - a.interestScore)
      .map((row, i) => {
        const { currentPosSet, otherInterestSet, ...rest } = row;
        const currentRoles =
          Array.from(currentPosSet).sort((a, b) => a.localeCompare(b)).join(", ") ||
          "—";
        const otherInterestRoles =
          Array.from(otherInterestSet).sort((a, b) => a.localeCompare(b)).join(", ") ||
          "—";
        return {
          ...rest,
          rank: i + 1,
          currentRoles,
          otherInterestRoles,
        };
      });
    return sorted;
  }, [hiringEvents, focusTargetRole]);

  const currentToTargetRows = useMemo(() => {
    type Row = {
      id: string;
      currentRole: string;
      targetRole: string;
      events: number;
      score: number;
      uniqueUsers: number;
    };
    const map = new Map<string, Row & { users: Set<string> }>();

    for (const ev of baseFiltered) {
      const cur = (ev.currentPos || "Unknown").trim() || "Unknown";
      const targets: string[] =
        ev.eventName === "COMPARE_POSITION"
          ? [ev.sourcePos, ev.targetPos].filter(
              (x): x is string => !!x && x.trim().length > 0
            )
          : ev.targetPos
            ? [ev.targetPos]
            : [];

      for (const tgt of targets) {
        const key = `${cur}::${tgt}`;
        if (!map.has(key)) {
          map.set(key, {
            id: key,
            currentRole: cur,
            targetRole: tgt,
            events: 0,
            score: 0,
            uniqueUsers: 0,
            users: new Set(),
          });
        }
        const r = map.get(key)!;
        r.events += 1;
        r.score += INTEREST_WEIGHT[ev.eventName];
        r.users.add(ev.userId);
        r.uniqueUsers = r.users.size;
      }
    }

    return Array.from(map.values())
      .map(({ users, ...rest }) => rest)
      .sort((a, b) => b.score - a.score);
  }, [baseFiltered]);

  const eventColumns: GridColDef<JobAnalyticsRow>[] = [
    { field: "id", headerName: "ID", width: 72, type: "number" },
    {
      field: "createdAt",
      headerName: "Time (UTC)",
      width: 178,
      valueFormatter: (v) => dayjs(v as string).format("YYYY-MM-DD HH:mm:ss"),
    },
    { field: "userId", headerName: "User", width: 88 },
    {
      field: "eventName",
      headerName: "Event",
      width: 130,
      renderCell: (p) => (
        <Chip
          size="small"
          label={EVENT_NAME_LABEL[p.value as JobAnalyticsEventName]}
          color={
            p.value === "FAVORITE"
              ? "success"
              : p.value === "COMPARE_POSITION"
                ? "secondary"
                : "info"
          }
          variant="outlined"
        />
      ),
    },
    {
      field: "currentPos",
      headerName: "Current role",
      flex: 1,
      minWidth: 160,
      valueGetter: (v) => v || "—",
    },
    {
      field: "sourcePos",
      headerName: "Source role",
      flex: 1,
      minWidth: 140,
      valueGetter: (v) => v || "N/A",
    },
    {
      field: "targetPos",
      headerName: "Target role",
      flex: 1,
      minWidth: 160,
      valueGetter: (v) => v || "—",
    },
    { field: "currentPosId", headerName: "Cur. ID", width: 84 },
    { field: "sourcePosId", headerName: "Src. ID", width: 84 },
    { field: "targetPosId", headerName: "Tgt. ID", width: 84 },
    { field: "appVersion", headerName: "App", width: 96 },
  ];

  const hiringCols: GridColDef<(typeof hiringRanked)[0]>[] = [
    { field: "rank", headerName: "Rank", width: 68, type: "number" },
    { field: "id", headerName: "User ID", width: 88 },
    {
      field: "currentRoles",
      headerName: "Current role(s)",
      flex: 1,
      minWidth: 160,
    },
    {
      field: "otherInterestRoles",
      headerName: "Other roles in same activity",
      flex: 1.2,
      minWidth: 200,
    },
    {
      field: "interestScore",
      headerName: "Score",
      width: 88,
      type: "number",
    },
    { field: "events", headerName: "Events", width: 80, type: "number" },
    { field: "views", headerName: "Views", width: 72, type: "number" },
    { field: "favorites", headerName: "Fav.", width: 64, type: "number" },
    { field: "compares", headerName: "Cmp.", width: 64, type: "number" },
  ];

  const flowCols: GridColDef<(typeof currentToTargetRows)[0]>[] = [
    { field: "currentRole", headerName: "Current role", flex: 1, minWidth: 180 },
    { field: "targetRole", headerName: "Target role", flex: 1, minWidth: 180 },
    { field: "uniqueUsers", headerName: "Users", width: 88, type: "number" },
    { field: "events", headerName: "Events", width: 88, type: "number" },
    { field: "score", headerName: "Score", width: 88, type: "number" },
  ];

  const demandCols: GridColDef<{
    id: string;
    rank: number;
    positionTitle: string;
    score: number;
    events: number;
  }>[] = [
    { field: "rank", headerName: "#", width: 56, type: "number" },
    { field: "positionTitle", headerName: "Target role", flex: 1, minWidth: 220 },
    { field: "score", headerName: "Score", width: 90, type: "number" },
    { field: "events", headerName: "Events", width: 90, type: "number" },
  ];

  const topDemandTableRows = useMemo(() => {
    type Agg = {
      positionTitle: string;
      score: number;
      events: number;
    };
    const map = new Map<string, Agg>();
    for (const ev of baseFiltered) {
      for (const pos of interestTargets(ev)) {
        const key = pos.title;
        if (!map.has(key)) map.set(key, { positionTitle: key, score: 0, events: 0 });
        const row = map.get(key)!;
        row.events += 1;
        row.score += INTEREST_WEIGHT[ev.eventName];
      }
    }
    return Array.from(map.values())
      .sort((a, b) => b.score - a.score)
      .slice(0, 25)
      .map((r, i) => ({
        id: r.positionTitle,
        rank: i + 1,
        positionTitle: r.positionTitle,
        score: r.score,
        events: r.events,
      }));
  }, [baseFiltered]);

  const pieData = [
    {
      id: "v",
      value: eventMix.VIEW_POSITION,
      label: "Views",
      color: theme.palette.info.main,
    },
    {
      id: "f",
      value: eventMix.FAVORITE,
      label: "Favorites",
      color: theme.palette.success.main,
    },
    {
      id: "c",
      value: eventMix.COMPARE_POSITION,
      label: "Compares",
      color: theme.palette.secondary.main,
    },
  ].filter((d) => d.value > 0);

  return (
    <Box className="box-border min-h-screen min-h-[100dvh] w-full bg-gray-50">
      <Stack spacing={4} sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, sm: 3 }, py: { xs: 3, md: 4 } }}>
        <Box>
          <Typography variant="h5" component="h1" fontWeight={600}>
            Job interest analytics
          </Typography>
        </Box>

        {loading && <LinearProgress sx={{ borderRadius: 1 }} />}
        {error && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => setRetryKey((k) => k + 1)}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={2}
          flexWrap="wrap"
          useFlexGap
          alignItems={{ xs: "stretch", lg: "flex-start" }}
        >
          <TextField
            label="From"
            type="date"
            size="small"
            value={dateFrom}
            onChange={handleDateFromChange}
            inputProps={{
              min: dateInputBounds.fromMin,
              max: dateInputBounds.fromMax,
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 152 }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={dateTo}
            onChange={handleDateToChange}
            inputProps={{
              min: dateInputBounds.toMin,
              max: dateInputBounds.toMax,
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 152 }}
          />
          <FormControl size="small" sx={{ minWidth: 148 }}>
            <InputLabel>Event</InputLabel>
            <Select
              label="Event"
              value={eventName}
              onChange={(e) =>
                setEventName(e.target.value as JobAnalyticsEventName | "")
              }
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="VIEW_POSITION">View</MenuItem>
              <MenuItem value="FAVORITE">Favorite</MenuItem>
              <MenuItem value="COMPARE_POSITION">Compare</MenuItem>
            </Select>
          </FormControl>
          <Autocomplete
            size="small"
            sx={{ flex: 1, minWidth: 280 }}
            options={positionOptions}
            value={focusTargetRole}
            onChange={(_, v) => setFocusTargetRole(v)}
            filterOptions={filterPositionOptions}
            openOnFocus={false}
            ListboxProps={{ style: { maxHeight: 280 } }}
            noOptionsText="No matching positions — keep typing"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Hiring target role"
                placeholder="Type to search positions…"
                helperText="Matches as you type; pick a role to load rankings."
              />
            )}
          />
        </Stack>

        <Card
          variant="outlined"
          sx={{
            bgcolor: "background.paper",
            borderColor: "divider",
          }}
        >
          <CardContent sx={{ py: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              TMs for selected target role
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 800 }}>
              Rankings for the hiring role you choose above. Each row shows where that TM works
              today, and any other roles touched in the same events (for example, the other side of
              a compare).
            </Typography>
            {!focusTargetRole ? (
              <Typography color="text.secondary" variant="body2">
                Type in the hiring field and select a target role to see TMs ranked by activity for
                that role.
              </Typography>
            ) : hiringRanked.length === 0 ? (
              <Typography color="text.secondary" variant="body2">
                No activity for this role in the selected range.
              </Typography>
            ) : (
              <DataGrid
                rows={hiringRanked}
                columns={hiringCols}
                disableRowSelectionOnClick
                pageSizeOptions={[10, 25, 50]}
                initialState={{
                  pagination: { paginationModel: { pageSize: 10, page: 0 } },
                }}
                sx={{
                  border: "none",
                  minHeight: 320,
                  "& .MuiDataGrid-virtualScroller": { maxHeight: 360 },
                }}
              />
            )}
          </CardContent>
        </Card>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} useFlexGap>
          <StatCard title="Events" value={kpis.totalEvents} />
          <StatCard title="Active users" value={kpis.uniqueUsers} hint={`Avg ${kpis.avgPerUser} events / user`} />
          <StatCard title="Target roles touched" value={kpis.distinctTargetRoles} />
        </Stack>

        <Card variant="outlined" sx={{ bgcolor: "background.paper", borderColor: "divider" }}>
          <CardContent sx={{ py: 2.5 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Event volume by day
            </Typography>
            {timeline.labels.length === 0 ? (
              <Typography color="text.secondary" variant="body2">
                No events in range
              </Typography>
            ) : (
              <LineChart
                xAxis={[
                  {
                    scaleType: "point",
                    data: timeline.labels,
                  },
                ]}
                series={[
                  {
                    data: timeline.values,
                    label: "Events",
                    color: theme.palette.primary.main,
                    area: true,
                    showMark: false,
                  },
                ]}
                height={260}
                grid={{ vertical: true, horizontal: true }}
                margin={{ left: 44, right: 16 }}
              />
            )}
          </CardContent>
        </Card>

        <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems="stretch">
          <Card
            variant="outlined"
            sx={{ flex: 1, minWidth: 0, bgcolor: "background.paper", borderColor: "divider" }}
          >
            <CardContent sx={{ py: 2.5, "&:last-child": { pb: 2 } }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Event mix
              </Typography>
              {pieData.length === 0 ? (
                <Typography color="text.secondary" variant="body2">
                  No data
                </Typography>
              ) : (
                <PieChart
                  height={300}
                  margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
                  series={[
                    {
                      data: pieData,
                      innerRadius: 44,
                      outerRadius: 92,
                      paddingAngle: 2,
                      cx: "50%",
                      cy: "44%",
                      highlightScope: { fade: "global", highlight: "item" },
                    },
                  ]}
                  slotProps={{
                    legend: {
                      direction: "horizontal",
                      position: { vertical: "bottom", horizontal: "center" },
                    },
                  }}
                  sx={{
                    "& .MuiChartsLegend-root": {
                      marginTop: 2,
                      paddingTop: 0,
                      justifyContent: "center",
                      flexWrap: "wrap",
                    },
                  }}
                />
              )}
            </CardContent>
          </Card>

          <Card
            variant="outlined"
            sx={{ flex: 2, minWidth: 0, bgcolor: "background.paper", borderColor: "divider" }}
          >
            <CardContent sx={{ py: 2.5 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Top target roles by engagement (stacked counts)
              </Typography>
              {topTargetJobs.labels.length === 0 ? (
                <Typography color="text.secondary" variant="body2">
                  No data
                </Typography>
              ) : (
                <BarChart
                  xAxis={[
                    {
                      scaleType: "band",
                      data: topTargetJobs.labels,
                      // Default axis height (~25px) is too small for angled labels: shortenLabels
                      // gets maxHeight ≈ 0 and ellipsizes every tick to "".
                      height: 76,
                      tickLabelStyle: {
                        angle: -32,
                        fontSize: 11,
                        fill: theme.palette.text.secondary,
                      },
                      tickLabelInterval: () => true,
                      tickLabelMinGap: 0,
                    },
                  ]}
                  series={[
                    {
                      data: topTargetJobs.views,
                      label: "Views",
                      stack: "total",
                      color: theme.palette.info.main,
                    },
                    {
                      data: topTargetJobs.favorites,
                      label: "Favorites",
                      stack: "total",
                      color: theme.palette.success.main,
                    },
                    {
                      data: topTargetJobs.compares,
                      label: "Compares",
                      stack: "total",
                      color: theme.palette.secondary.main,
                    },
                  ]}
                  height={300}
                  margin={{ left: 8, right: 12, top: 8, bottom: 16 }}
                  grid={{ vertical: true, horizontal: true }}
                />
              )}
            </CardContent>
          </Card>
        </Stack>

        <Card variant="outlined" sx={{ bgcolor: "background.paper", borderColor: "divider" }}>
          <CardContent sx={{ py: 2.5 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Top jobs in demand (by score)
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Target roles with the most interest signals in the date range.
            </Typography>
            <DataGrid
              rows={topDemandTableRows}
              columns={demandCols}
              disableRowSelectionOnClick
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: { paginationModel: { pageSize: 10, page: 0 } },
              }}
              sx={{
                border: "none",
                minHeight: 280,
                "& .MuiDataGrid-virtualScroller": { maxHeight: 280 },
              }}
            />
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ bgcolor: "background.paper", borderColor: "divider" }}>
          <CardContent sx={{ py: 2.5 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Current role → target role
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Aggregated flows: which roles people work in vs roles they are exploring.
            </Typography>
            <DataGrid
              rows={currentToTargetRows}
              columns={flowCols}
              disableRowSelectionOnClick
              pageSizeOptions={[25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25, page: 0 } },
              }}
              sx={{
                border: "none",
                minHeight: 360,
                "& .MuiDataGrid-virtualScroller": { maxHeight: 300 },
              }}
            />
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ bgcolor: "background.paper", borderColor: "divider" }}>
          <CardContent sx={{ py: 2.5 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Event log
            </Typography>
            <DataGrid
              rows={baseFiltered}
              columns={eventColumns}
              getRowId={(r) => r.id}
              disableRowSelectionOnClick
              pageSizeOptions={[25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25, page: 0 } },
              }}
              sx={{
                border: "none",
                minHeight: 380,
                "& .MuiDataGrid-virtualScroller": { maxHeight: 320 },
              }}
            />
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
};

export default TMHubReport;
